// MACHINERY V2 (Stroje) — Fleet V2 group section + health header (M2.3).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.3 + §9 D4. Reference impl:
// design_handoff_machinery_v3/machinery-v2-grouped.jsx (V2GroupHeader,
// SummaryChip).
//
// >>> THIS IS THE ONLY SECTION DESIGN. <<< §9 D4's consistency rule: every
// carried grouping dimension (type / location / line / department / all) renders
// through THIS component. There is no per-dimension section variant, because the
// designer only drew one and inventing four would be exactly the divergence D4
// was written to prevent.
//
// HEALTH COUNTS COME FROM THE CONTRACT, NOT FROM TELEMETRY. The `states` map is
// built by fleetSelectors.deriveFleetStates -> deriveCardPresentation, and
// countStates buckets it. This component never sees a raw telemetry field, so
// the header cannot disagree with the badges in the rows beneath it.
//
// ONE MACHINE, ONE CHIP — INCLUDING offline+alarms. A machine that is offline
// AND carrying alarms resolves to `offline`, because the connectivity verdict
// outranks alarms in deriveCardState (the M2.1 fix, commit f1242dd7). It
// therefore counts under OFFLINE and is deliberately absent from the alarm
// count: the alarm is stale information about a machine we can no longer reach,
// and adding it to "Y alarm" would tell a supervisor to go look at a live fault
// that may have cleared hours ago. The row still SHOWS the alarm, marked
// last-known (presentation.alarmLastKnown) — filtered as offline, displayed as
// offline-with-a-last-known-alarm. `stale-alarm` (reachable, stale data, alarm
// present) keeps its own chip and is NOT folded into `alarm` either — same
// one-canonical-state-per-cell discipline as the M2.2 LiveFleetStrip.
//
// The artboard always renders the running + idle chips and shows the rest only
// when non-zero. We keep that, expressed over the canonical state vocabulary
// (fleetStatusFilters.FLEET_STATE_FILTERS) so the header, the strip and the rail
// can never offer different state names.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { Machine } from "../../models/models";
import { MachineryV2ReadinessPresentation } from "../readiness/readinessPresentation";
import FleetRow from "./FleetRow";
import { FleetMenuAction } from "./FleetCardMenu";
import { MachineryV2CardState, MachineryV2Telemetry, unconnectedTelemetry } from "./cardStates";
import { countStates } from "./fleetSelectors";
import { FLEET_STATE_FILTERS, filterInk } from "./fleetStatusFilters";

/** States whose chip renders even at zero — the artboard's "always visible"
 *  pair. Everything else appears only when it has members. */
const ALWAYS_SHOWN: MachineryV2CardState[] = ["running", "idle"];

const SummaryChip: React.FC<{ n: number; ink: string; label: string }> = ({ n, ink, label }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
    <span style={{ width: 6, height: 6, borderRadius: 999, background: ink, flexShrink: 0 }} />
    <span style={{ color: "var(--ink-2)", fontWeight: 600 }}>{n}</span>
    <span style={{ color: "var(--faint)" }}>{label}</span>
  </span>
);

export interface FleetGroupSectionProps {
  title: string;
  machines: Machine[];
  states: Map<string, MachineryV2CardState>;
  telemetry: Map<string, MachineryV2Telemetry>;
  readiness: Map<string, MachineryV2ReadinessPresentation>;
  newMachineId: string | null;
  collapsed: boolean;
  onToggle: () => void;
  buildActions: (m: Machine) => FleetMenuAction[];
  onOpenProfile: (m: Machine) => void;
  onOpenReadiness: (m: Machine) => void;
}

const FleetGroupSection: React.FC<FleetGroupSectionProps> = ({
  title, machines, states, telemetry, readiness, newMachineId, collapsed,
  onToggle, buildActions, onOpenProfile, onOpenReadiness,
}) => {
  const { translate } = useTranslation("myMachinery");
  const counts = countStates(machines, states);

  const chips = FLEET_STATE_FILTERS.filter(
    (s) => (counts[s.state] || 0) > 0 || ALWAYS_SHOWN.indexOf(s.state) >= 0
  );

  return (
    <div style={{ minWidth: 0 }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 12,
          padding: "12px 16px", border: 0, borderLeft: "3px solid var(--mod-accent)",
          borderRadius: 8, background: "var(--mod-accent-soft)", cursor: "pointer",
          fontFamily: "inherit", textAlign: "left", marginBottom: 10, flexWrap: "wrap",
        }}
      >
        <span style={{ color: "var(--mod-accent)", fontSize: 12 }}>
          {collapsed ? "▸" : "▾"}
        </span>
        <span
          style={{
            fontWeight: 600, color: "var(--ink)", fontSize: 14.5, minWidth: 0,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}
        >
          {title}
        </span>
        <span
          style={{
            fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--mod-accent)",
            background: "var(--card)", padding: "2px 8px", borderRadius: 999,
            border: "1px solid var(--mod-accent-line)", flexShrink: 0,
          }}
        >
          {translate("myMachinery:v2.fleet.machineCount", { count: machines.length })}
        </span>
        <span style={{ flex: 1, minWidth: 12 }} />
        <span
          style={{
            display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
            fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--muted)",
          }}
        >
          {chips.map((s) => (
            <SummaryChip
              key={s.state}
              n={counts[s.state] || 0}
              ink={filterInk(s.token)}
              label={translate(s.labelKey)}
            />
          ))}
        </span>
      </button>

      {!collapsed && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
          {machines.map((m) => (
            <FleetRow
              key={m.id}
              machine={m}
              telemetry={telemetry.get(m.id) || unconnectedTelemetry()}
              readiness={readiness.get(m.id) || null}
              isNew={m.id === newMachineId}
              actions={buildActions(m)}
              onOpenProfile={() => onOpenProfile(m)}
              onOpenReadiness={() => onOpenReadiness(m)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default FleetGroupSection;
